import React, { useState, useCallback } from 'react';
import ConfirmContext from './ConfirmContext';
import ConfirmationDialog from './ConfirmationDialog';
import TextInputDialogContext from './TextInputDialogContext';
import TextInputDialog from './TextInputDialog';
import type {
  ConfirmProviderProps,
  ConfirmResult,
  ConfirmOptions,
  TextInputDialogOptions,
  TextInputDialogResult,
} from '../types';

const DEFAULT_OPTIONS = {
  title: 'Are you sure?',
  description: '',
  content: null,
  confirmationText: 'Ok',
  secondaryText: 'Secondary',
  cancellationText: 'Cancel',
  dialogProps: {},
  dialogActionsProps: {},
  confirmationButtonProps: {},
  secondaryButtonProps: {},
  cancellationButtonProps: {},
  titleProps: {},
  contentProps: {},
  allowClose: true,
  confirmationKeywordTextFieldProps: {},
  hideCancelButton: false,
  showSecondaryButton: false,
  buttonOrder: ['cancel', 'secondary', 'confirm'],
  acknowledgement: false,
  acknowledgementFormControlLabelProps: {},
  acknowledgementCheckboxProps: {},
};

const buildOptions = (defaultOptions, options) => {
  const dialogProps = {
    ...(defaultOptions.dialogProps || DEFAULT_OPTIONS.dialogProps),
    ...(options.dialogProps || {}),
  };
  const dialogActionsProps = {
    ...(defaultOptions.dialogActionsProps || DEFAULT_OPTIONS.dialogActionsProps),
    ...(options.dialogActionsProps || {}),
  };
  const confirmationButtonProps = {
    ...(defaultOptions.confirmationButtonProps || DEFAULT_OPTIONS.confirmationButtonProps),
    ...(options.confirmationButtonProps || {}),
  };
  const secondaryButtonProps = {
    ...(defaultOptions.secondaryButtonProps || DEFAULT_OPTIONS.secondaryButtonProps),
    ...(options.secondaryButtonProps || {}),
  };
  const cancellationButtonProps = {
    ...(defaultOptions.cancellationButtonProps || DEFAULT_OPTIONS.cancellationButtonProps),
    ...(options.cancellationButtonProps || {}),
  };
  const titleProps = {
    ...(defaultOptions.titleProps || DEFAULT_OPTIONS.titleProps),
    ...(options.titleProps || {}),
  };
  const contentProps = {
    ...(defaultOptions.contentProps || DEFAULT_OPTIONS.contentProps),
    ...(options.contentProps || {}),
  };
  const confirmationKeywordTextFieldProps = {
    ...(defaultOptions.confirmationKeywordTextFieldProps ||
      DEFAULT_OPTIONS.confirmationKeywordTextFieldProps),
    ...(options.confirmationKeywordTextFieldProps || {}),
  };
  const acknowledgementFormControlLabelProps = {
    ...(defaultOptions.acknowledgementFormControlLabelProps ||
      DEFAULT_OPTIONS.acknowledgementFormControlLabelProps),
    ...(options.acknowledgementFormControlLabelProps || {}),
  };
  const acknowledgementCheckboxProps = {
    ...(defaultOptions.acknowledgementCheckboxProps ||
      DEFAULT_OPTIONS.acknowledgementCheckboxProps),
    ...(options.acknowledgementCheckboxProps || {}),
  };

  return {
    ...DEFAULT_OPTIONS,
    ...defaultOptions,
    ...options,
    dialogProps,
    dialogActionsProps,
    confirmationButtonProps,
    secondaryButtonProps,
    cancellationButtonProps,
    titleProps,
    contentProps,
    confirmationKeywordTextFieldProps,
    acknowledgementFormControlLabelProps,
    acknowledgementCheckboxProps,
  };
};

let confirmGlobal: (options: ConfirmOptions) => Promise<ConfirmResult>;

const ConfirmProvider = ({ children, defaultOptions = {} }: ConfirmProviderProps) => {
  // ============================================================================
  // Confirm Dialog State
  // ============================================================================

  // State that we clear on close (to avoid dangling references to resolve and
  // reject). If this is null, the dialog is closed.
  const [state, setState] = useState<{
    resolve: (result: ConfirmResult) => void;
    parentId: string;
  } | null>(null);
  // Options for rendering the dialog, which aren't reset on close so that we
  // keep rendering the same modal during close animation
  const [options, setOptions] = useState<ConfirmOptions>({});
  const [key, setKey] = useState(0);

  // ============================================================================
  // Dialog Text Input State
  // ============================================================================

  const [textInputState, setTextInputState] = useState<{
    resolve: (result: TextInputDialogResult) => void;
    parentId: string;
  } | null>(null);
  const [textInputOptions, setTextInputOptions] = useState<TextInputDialogOptions>({ inputs: [] });
  const [textInputKey, setTextInputKey] = useState(0);

  const confirmBase = useCallback((parentId: string, options: ConfirmOptions = {}) => {
    const promise = new Promise<ConfirmResult>((resolve) => {
      setKey((key) => key + 1);
      setOptions(options);
      setState({ resolve, parentId });
    });

    return promise;
  }, []);

  const closeOnParentUnmount = useCallback((parentId: string) => {
    setState((state) => {
      if (state?.parentId === parentId) {
        state?.resolve({ confirmed: false, reason: 'unmount' });
        return null;
      } else {
        return state;
      }
    });
  }, []);

  const handleClose = useCallback(() => {
    setState((state) => {
      state?.resolve({ confirmed: false, reason: 'natural' });
      return null;
    });
  }, []);

  const handleCancel = useCallback(() => {
    setState((state) => {
      state?.resolve({ confirmed: false, reason: 'cancel' });
      return null;
    });
  }, []);

  const handleSecondary = useCallback(() => {
    setState((state) => {
      state?.resolve({ confirmed: false, reason: 'secondary' });
      return null;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setState((state) => {
      state?.resolve({ confirmed: true, reason: 'confirm' });
      return null;
    });
  }, []);

  confirmGlobal = useCallback((options: ConfirmOptions) => {
    return confirmBase('global', options);
  }, []);

  // ============================================================================
  // Dialog Text Input Callbacks
  // ============================================================================

  const openTextInputDialogBase = useCallback(
    <
      T extends Record<string, string | number | boolean> = Record<
        string,
        string | number | boolean
      >,
    >(
      parentId: string,
      inputOptions: TextInputDialogOptions,
    ): Promise<TextInputDialogResult<T>> => {
      return new Promise<TextInputDialogResult<T>>((resolve) => {
        setTextInputKey((k) => k + 1);
        setTextInputOptions(inputOptions);
        setTextInputState({
          resolve: resolve as (result: TextInputDialogResult) => void,
          parentId,
        });
      });
    },
    [],
  );

  const closeTextInputDialogOnParentUnmount = useCallback((parentId: string) => {
    setTextInputState((currentState) => {
      if (currentState?.parentId === parentId) {
        currentState.resolve(null);
        return null;
      }
      return currentState;
    });
  }, []);

  const handleTextInputConfirm = useCallback(
    (values: Record<string, string | number | boolean>) => {
      setTextInputState((currentState) => {
        currentState?.resolve(values);
        return null;
      });
    },
    [],
  );

  const handleTextInputCancel = useCallback(() => {
    setTextInputState((currentState) => {
      currentState?.resolve(null);
      return null;
    });
  }, []);

  return (
    <>
      <ConfirmContext.Provider value={{ confirmBase, closeOnParentUnmount }}>
        <TextInputDialogContext.Provider
          value={{ openTextInputDialogBase, closeTextInputDialogOnParentUnmount }}
        >
          {children}
        </TextInputDialogContext.Provider>
      </ConfirmContext.Provider>
      <ConfirmationDialog
        key={`confirm-${key}`}
        open={state !== null}
        options={buildOptions(defaultOptions, options ?? {})}
        onClose={handleClose}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        onSecondary={handleSecondary}
      />
      <TextInputDialog
        key={`text-input-${textInputKey}`}
        open={textInputState !== null}
        options={textInputOptions}
        onConfirm={handleTextInputConfirm}
        onCancel={handleTextInputCancel}
      />
    </>
  );
};

export { ConfirmProvider, confirmGlobal as confirm };
